/**
 * Loads and shapes app_config + condition_config from Supabase.
 * RLS allows authenticated users to read both tables.
 */

import { useQuery } from "@tanstack/react-query";
import { supabase } from "../lib/supabase";
import type { AppConfigMap, ConditionConfigRow } from "../types";

interface ConfigState {
  loading: boolean;
  config: AppConfigMap | null;
  conditionConfig: ConditionConfigRow[];
}

const DEFAULTS: AppConfigMap = {
  staff_start_date: "",
  volunteer_start_date: "",
  staff_window_days: 7,
  volunteer_window_days: 14,
  global_broadcast_note: "",
  asana_bug_form_url: "",
  contact_email: "app.testing@teamrwb.org",
};

export function useAppConfig(enabled: boolean = true): ConfigState {
  const configQuery = useQuery<AppConfigMap>({
    queryKey: ["app-config"],
    enabled,
    queryFn: async () => {
      const { data, error } = await supabase.from("app_config").select("key,value");
      if (error) throw error;
      return shapeAppConfig(data ?? []);
    },
    staleTime: 30_000,
  });

  const condQuery = useQuery<ConditionConfigRow[]>({
    queryKey: ["condition-config"],
    enabled,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("condition_config")
        .select("*")
        .eq("active", true)
        .order("section")
        .order("sort_order");
      if (error) throw error;
      return (data ?? []) as ConditionConfigRow[];
    },
    staleTime: 60_000,
  });

  return {
    loading: configQuery.isLoading || condQuery.isLoading,
    config: configQuery.data ?? null,
    conditionConfig: condQuery.data ?? [],
  };
}

function shapeAppConfig(rows: Array<{ key: string; value: string }>): AppConfigMap {
  const map = { ...DEFAULTS };
  for (const row of rows) {
    switch (row.key) {
      case "staff_start_date":
      case "volunteer_start_date":
      case "global_broadcast_note":
      case "asana_bug_form_url":
      case "contact_email":
        map[row.key] = row.value;
        break;
      case "staff_window_days":
      case "volunteer_window_days": {
        const n = parseInt(row.value, 10);
        if (!Number.isNaN(n) && n > 0) map[row.key] = n;
        break;
      }
    }
  }
  return map;
}
